import React from 'react';
import { Table, TableScroll, Badge, SortableTh, RowActions, ACTIONS_COL_WIDTH, EmptyValue, ServiceAreaPill } from '@/components/ui';
import type { User } from '../../../types/User';
import { usePermissions } from '../../../hooks/usePermissions';

// Test//

interface UsersTableProps {
  users: User[];
  onRowClick: (user: User) => void;
  onViewHistory?: (user: User) => void;
  onActivate?: (user: User) => void;
  onDeactivate?: (user: User) => void;
  onSeasonChange?: (user: User, season: 'summer' | 'winter') => void;
  season?: 'summer' | 'winter' | null;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onRowClick,
  onViewHistory,
  onActivate,
  onDeactivate,
  onSeasonChange,
  season = null,
  sortBy = null,
  sortDir = 'asc',
  onSort
}) => {
  const { hasPermission } = usePermissions();
  const canEditUsers = hasPermission('user:edit') || hasPermission('employee:edit');

  const handleRowClick = (user: User, event: React.MouseEvent) => {
    // Don't trigger row click if clicking on buttons
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    onRowClick(user);
  };

  // History / activate / deactivate no longer need stopPropagation wrappers — RowActions
  // stops row-click propagation for the whole actions cell.

  // Get user's active season (default to summer if not set)
  const getUserActiveSeason = (user: User): 'summer' | 'winter' => {
    return (user as any).activeSeason || 'summer';
  };

  return (
    // TableScroll is the one scroll box on both axes (10 columns cannot fit a phone): the sticky
    // header and the pinned Employee Name / Actions columns attach to it, and the horizontal
    // scrollbar sits at its bottom. Put it in ListPage.Body so it fills the height.
    <TableScroll>
      <Table hover stickyHeader className="tw-mb-0">
        <thead>
          <tr>
            <SortableTh label="Employee Name" sortKey="name" activeKey={sortBy} direction={sortDir} onSort={onSort} className="wrap-pin-left wrap-pin-edge" style={{ minWidth: '200px' }} />
            <SortableTh label="Emp #" sortKey="employeeNumber" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ width: '90px' }} />
            <th style={{ width: '140px' }}>Service Area</th>
            <SortableTh label="Email Address" sortKey="email" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Role" sortKey="role" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <th>Shift Length</th>
            <SortableTh label="Season" sortKey="activeSeason" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Portal User" sortKey="hasPortalAccess" activeKey={sortBy} direction={sortDir} onSort={onSort} className="tw-text-center" style={{ width: '110px' }} />
            <SortableTh label="Active" sortKey="status" activeKey={sortBy} direction={sortDir} onSort={onSort} className="tw-text-center" style={{ width: '90px' }} />
            <th className="wrap-pin-right tw-text-center" style={{ width: ACTIONS_COL_WIDTH }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 && (
            <tr>
              <td colSpan={10} className="tw-text-center tw-py-4">
                <i className="bi bi-people tw-text-6xl tw-font-light tw-text-content-muted"></i>
                <h5 className="tw-mt-3">No employees found</h5>
                <p className="tw-text-content-muted tw-mb-0">
                  Try adjusting your search terms or filters.
                </p>
              </td>
            </tr>
          )}
          {users.map((user) => (
            <tr 
              key={user.id} 
              onClick={(e) => handleRowClick(user, e)} 
              style={{ cursor: 'pointer' }}
              className={user.status === 'DISABLED' ? 'tw-bg-surface-alt' : ''}
            >
              <td className="wrap-pin-left wrap-pin-edge" style={{ whiteSpace: 'nowrap' }}>{user.name} {user.lastName}</td>
              <td>{user.employeeNumber || ''}</td>
              <td>
                {user.foremanAreas && user.foremanAreas.length > 0 ? (
                  <div className="tw-flex tw-flex-wrap tw-gap-1">
                    {Array.from(new Set(
                      user.foremanAreas
                        .map(fa => fa.serviceArea?.name || fa.serviceArea?.code || `SA ${fa.serviceAreaId}`)
                        .filter(Boolean)
                    )).map((serviceAreaName, index) => (
                      <ServiceAreaPill key={index} value={serviceAreaName} />
                    ))}
                  </div>
                ) : (
                  <EmptyValue />
                )}
              </td>
              <td>{user.email}</td>
              <td>
                {user.role?.name ? (
                  <Badge bg="primary">{user.role.name}</Badge>
                ) : (
                  <span className="tw-text-content-muted">-</span>
                )}
              </td>
              <td>
                {(() => {
                  const userActiveSeason = getUserActiveSeason(user);
                  return userActiveSeason === 'summer'
                    ? (user.summerShiftLength != null ? `${Number(user.summerShiftLength).toFixed(2)}h` : '-')
                    : userActiveSeason === 'winter'
                    ? (user.winterShiftLength != null ? `${Number(user.winterShiftLength).toFixed(2)}h` : '-')
                    : '-';
                })()}
              </td>
              <td>
                {/* Read-only in the row (W-M18): the season is changed from the row menu, so the
                    cell no longer looks like two live toggle buttons on every row. */}
                <Badge pill bg={getUserActiveSeason(user) === 'summer' ? 'warning' : 'primary'}>
                  <i className={`bi ${getUserActiveSeason(user) === 'summer' ? 'bi-sun' : 'bi-snow'}`} aria-hidden="true"></i>
                  {getUserActiveSeason(user) === 'summer' ? 'Summer' : 'Winter'}
                </Badge>
              </td>
              <td className="tw-text-center">
                {/* "No" is a normal state, not an error: neutral, not a red X (W-M18). */}
                {user.hasPortalAccess ? (
                  <i className="bi bi-check-circle-fill tw-text-success" style={{ fontSize: '1.5rem' }} role="img" aria-label="Yes"></i>
                ) : (
                  <span className="tw-text-content-muted" role="img" aria-label="No">
                    <i className="bi bi-dash-circle" style={{ fontSize: '1.5rem' }} aria-hidden="true"></i>
                  </span>
                )}
              </td>
              <td className="tw-text-center">
                {user.status === 'ACTIVE' ? (
                  <i className="bi bi-check-circle-fill tw-text-success" style={{ fontSize: '1.5rem' }}></i>
                ) : (
                  <i className="bi bi-x-circle-fill tw-text-danger" style={{ fontSize: '1.5rem' }}></i>
                )}
              </td>
              <td className="wrap-pin-right tw-text-center">
                <RowActions
                  align="left"
                  className="tw-justify-center"
                  actions={[
                    { kind: 'edit', label: 'Edit User', onClick: () => onRowClick(user) },
                    {
                      kind: 'custom',
                      icon: getUserActiveSeason(user) === 'summer' ? 'bi-snow' : 'bi-sun',
                      label: getUserActiveSeason(user) === 'summer' ? 'Switch to Winter' : 'Switch to Summer',
                      onClick: () => onSeasonChange?.(user, getUserActiveSeason(user) === 'summer' ? 'winter' : 'summer'),
                      hidden: !(canEditUsers && onSeasonChange),
                    },
                    {
                      kind: 'history',
                      label: 'View Change History',
                      onClick: () => onViewHistory?.(user),
                      hidden: !onViewHistory,
                    },
                    {
                      kind: 'disable',
                      icon: 'bi-person-slash',
                      label: 'Deactivate User',
                      onClick: () => onDeactivate?.(user),
                      hidden: !hasPermission('user:edit') || user.status !== 'ACTIVE' || !onDeactivate,
                    },
                    {
                      kind: 'enable',
                      icon: 'bi-person-check',
                      label: 'Activate User',
                      onClick: () => onActivate?.(user),
                      hidden: !hasPermission('user:edit') || user.status === 'ACTIVE' || !onActivate,
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
};

export default UsersTable;
